import React from "react";
import { Box } from "@mui/material";
import { toast } from "react-toastify";
import { Field } from "../GenericFormDialog";
import CustomTextField from "../CustomTextField";
import CustomSelect from "../CustomSelect";
import CustomLabel from "../CustomLabel";
import {
  ACTIVE_OPTIONS,
  ENROLLMENT_MODE_OPTIONS,
  RECURRENCE_FREQUENCY_OPTIONS,
  WEEK_DAY_OPTIONS,
  addMinutes,
  combineDateAndTime,
  isDateBeforeToday,
  toActiveLabel,
  toDateInputValue,
} from "./classUtils";
import {
  ClassDefinition,
  CreateClassPayload,
  EnrollmentMode,
  RecurrenceFrequency,
  RecurrencePayload,
  UpdateClassPayload,
  UpdateSchedulePayload,
} from "./types";
import { Option } from "./useClassOptions";

/**
 * Shared field/value/payload building for the "Add Class" form — used both by
 * the standalone dialog on the Classes page and by the calendar's quick-add
 * dialog, which mixes it in as a third tab alongside Add Appointment / Block
 * Slots. Field names are prefixed with `class_` because the calendar dialog
 * keeps one flat values object across all three tabs, and several of these
 * names (recurrence_interval, recurrence_week_days, ...) collide with the
 * appointment tab's own recurring-appointment fields otherwise.
 */

export const REPEAT_OPTIONS = [
  { label: "Does not repeat", value: "NONE" },
  ...RECURRENCE_FREQUENCY_OPTIONS,
];

/**
 * NEVER sends neither `recurrence_end` nor `recurrence_count`, which is what
 * makes the class open-ended: no session rows are written up front, dates are
 * projected from the rule and only become real when someone books them.
 */
export const END_MODE_OPTIONS = [
  { label: "After a number of sessions", value: "COUNT" },
  { label: "On a date", value: "DATE" },
  { label: "Runs until further notice", value: "NEVER" },
];

export const MONTH_DAY_OPTIONS = Array.from({ length: 31 }, (_, i) => ({
  label: String(i + 1),
  value: String(i + 1),
}));

export const toStringArray = (value: any): string[] => {
  if (Array.isArray(value)) return value.map((v) => String(v));
  if (value === undefined || value === null || value === "") return [];
  return String(value)
    .split(",")
    .map((v) => v.trim())
    .filter(Boolean);
};

const renderMultiSelect = (
  label: string,
  options: { label: string; value: string }[],
  required?: boolean
) => (value: any, onChange: (value: any) => void, _helpers?: any, error?: string) =>
  (
    <Box>
      <CustomLabel
        text={label}
        fontSize={14}
        fontWeight={500}
        highlightLabelStar={Boolean(required)}
      />
      <CustomSelect
        value={toStringArray(value)}
        options={options}
        multiSelect
        onChange={(v: any) => onChange(toStringArray(v))}
        error={Boolean(error)}
        helperText={error}
        placeholder="Select..."
        fullWidth
      />
    </Box>
  );

const renderTimeField = (
  label: string
) => (value: any, onChange: (value: any) => void, _helpers?: any, error?: string) =>
  (
    <Box>
      <CustomLabel text={label} fontSize={14} fontWeight={500} highlightLabelStar />
      <CustomTextField
        type="time"
        value={value ?? ""}
        onChange={(e: any) => onChange(e.target.value)}
        error={Boolean(error)}
        helperText={error}
        fullWidth
      />
    </Box>
  );

/** Reads the two bits of local state (repeat frequency, series-end mode) a caller needs to re-derive the field list as the user fills the form. */
export const readClassFormControlState = (values: Record<string, any>) => ({
  frequency: String(values.class_repeat || "NONE"),
  endMode: String(values.class_end_mode || "COUNT"),
});

/**
 * When the class runs. Shared by the create form and the reschedule dialog —
 * the latter drops the unit, since moving a class to another court is a
 * different operation than moving it in time.
 */
export const buildClassScheduleFields = ({
  unitOptions,
  frequency,
  endMode,
  startDateLabel = "First Session Date",
}: {
  unitOptions?: Option[];
  frequency: string;
  endMode: string;
  startDateLabel?: string;
}): Field[] => {
  const fields: Field[] = [];

  if (unitOptions) {
    fields.push({
      name: "class_unit_id",
      label: "Unit / Court",
      type: "dropdown",
      options: unitOptions,
      searchable: true,
      required: true,
    });
  }

  fields.push(
    {
      name: "class_start_date",
      label: startDateLabel,
      type: "date",
      required: true,
    },
    {
      name: "class_start_time",
      label: "Start Time",
      required: true,
      customRender: renderTimeField("Start Time"),
    },
    {
      name: "class_duration",
      label: "Duration (minutes)",
      type: "number",
      required: true,
    },
    {
      name: "class_repeat",
      label: "Repeat",
      type: "dropdown",
      options: REPEAT_OPTIONS,
      required: true,
    }
  );

  if (frequency === "NONE") return fields;

  fields.push({
    name: "class_recurrence_interval",
    label:
      frequency === "DAILY"
        ? "Repeat Every (Days)"
        : frequency === "WEEKLY"
        ? "Repeat Every (Weeks)"
        : "Repeat Every (Months)",
    type: "number",
  });

  if (frequency === "WEEKLY") {
    fields.push({
      name: "class_recurrence_week_days",
      label: "Days of the Week",
      required: true,
      customRender: renderMultiSelect("Days of the Week", WEEK_DAY_OPTIONS, true),
    });
  }

  if (frequency === "MONTHLY") {
    fields.push({
      name: "class_recurrence_month_days",
      label: "Days of the Month",
      required: true,
      customRender: renderMultiSelect("Days of the Month", MONTH_DAY_OPTIONS, true),
    });
  }

  fields.push({
    name: "class_end_mode",
    label: "Stops After",
    type: "dropdown",
    options: END_MODE_OPTIONS,
    required: true,
  });

  if (endMode === "DATE") {
    fields.push({
      name: "class_recurrence_end",
      label: "Last Session Date",
      type: "date",
      required: true,
    });
  } else if (endMode === "COUNT") {
    fields.push({
      name: "class_recurrence_count",
      label: "Number of Sessions",
      type: "number",
      required: true,
    });
  }

  return fields;
};

export const buildClassFormFields = ({
  isEdit,
  unitOptions,
  coachOptions,
  categoryOptions,
  frequency,
  endMode,
}: {
  isEdit: boolean;
  unitOptions: Option[];
  coachOptions: Option[];
  categoryOptions: Option[];
  frequency: string;
  endMode: string;
}): Field[] => {
  const definitionFields: Field[] = [
    { name: "class_name", label: "Class Name", required: true },
    {
      name: "class_coach_id",
      label: "Coach",
      type: "dropdown",
      options: coachOptions,
      searchable: true,
      required: true,
    },
    {
      name: "class_category_id",
      label: "Category (optional)",
      type: "dropdown",
      options: categoryOptions,
      searchable: true,
    },
    {
      name: "class_enrollment_mode",
      label: "Enrollment Mode",
      type: "dropdown",
      options: ENROLLMENT_MODE_OPTIONS.map((o) => ({
        label: o.label,
        value: o.value,
      })),
      required: true,
    },
    { name: "class_capacity", label: "Capacity", type: "number", required: true },
    {
      name: "class_price",
      label: "Drop-in Price",
      type: "number",
      required: true,
    },
  ];

  if (isEdit) {
    return [
      ...definitionFields,
      {
        name: "class_active",
        label: "Status",
        type: "dropdown",
        options: ACTIVE_OPTIONS,
        required: true,
      },
      { name: "class_description", label: "Description", colSpan: 2 },
    ];
  }

  return [
    ...definitionFields,
    ...buildClassScheduleFields({ unitOptions, frequency, endMode }),
    { name: "class_description", label: "Description", colSpan: 2 },
  ];
};

export const buildClassFormInitialValues = ({
  classItem,
  defaultUnitId,
  defaultStartDate,
  defaultStartTime,
}: {
  classItem: ClassDefinition | null;
  defaultUnitId?: string;
  defaultStartDate?: string;
  defaultStartTime?: string;
}): Record<string, any> => {
  if (classItem) {
    return {
      class_name: String(classItem.name || ""),
      class_coach_id: String(classItem.coach_id ?? classItem.coach?.id ?? ""),
      class_category_id: String(
        classItem.category_id ?? classItem.category?.id ?? ""
      ),
      class_enrollment_mode: String(classItem.enrollment_mode || "BOTH"),
      class_capacity: String(classItem.capacity ?? ""),
      class_price: String(classItem.price ?? ""),
      class_active: toActiveLabel(classItem.active),
      class_description: String(classItem.description || ""),
    };
  }
  return {
    class_name: "",
    class_coach_id: "",
    class_category_id: "",
    class_enrollment_mode: "BOTH",
    class_capacity: "",
    class_price: "",
    class_unit_id: defaultUnitId ? String(defaultUnitId) : "",
    class_start_date: defaultStartDate ? String(defaultStartDate) : "",
    class_start_time: defaultStartTime ? String(defaultStartTime) : "",
    class_duration: "60",
    class_repeat: "NONE",
    class_recurrence_interval: "1",
    class_recurrence_week_days: [],
    class_recurrence_month_days: [],
    class_end_mode: "COUNT",
    class_recurrence_count: "8",
    class_recurrence_end: "",
    class_description: "",
  };
};

/**
 * Pre-fills the reschedule dialog from the rule the class already carries, so
 * "move it half an hour later" doesn't mean retyping the whole recurrence.
 */
export const buildClassScheduleInitialValues = (
  classItem: ClassDefinition | null,
  /**
   * The class's next real occurrence, when the caller managed to load one. The
   * class row doesn't reliably carry `start_at` — the list endpoint often
   * omits it — but a session always carries `start_at` and `end_at`, which
   * makes it the trustworthy answer to "when does this currently run, and for
   * how long". Preferred over the class row whenever it's available.
   */
  nextSession?: { start_at?: string; end_at?: string } | null
): Record<string, any> => {
  const startSource =
    classItem?.start_at ||
    classItem?.recurrence_start ||
    classItem?.upcoming_sessions?.[0]?.start_at;
  const start = startSource ? new Date(startSource) : null;
  const hasStart = Boolean(start) && !Number.isNaN(start!.getTime());

  const sessionStart = nextSession?.start_at
    ? new Date(nextSession.start_at)
    : null;
  const hasSessionStart =
    Boolean(sessionStart) && !Number.isNaN(sessionStart!.getTime());
  const sessionEnd = nextSession?.end_at ? new Date(nextSession.end_at) : null;
  const sessionDuration =
    hasSessionStart && sessionEnd && !Number.isNaN(sessionEnd.getTime())
      ? Math.round((sessionEnd.getTime() - sessionStart!.getTime()) / 60000)
      : null;

  const endMode = classItem?.recurrence_end
    ? "DATE"
    : classItem?.recurrence_count
    ? "COUNT"
    : classItem?.recurrence_frequency
    ? "NEVER"
    : "COUNT";

  // The next occurrence first — it is both real and in the future. Failing
  // that, the stored anchor if it hasn't already passed (a long-running class's
  // is months old), and today as the last resort. Rescheduling only ever
  // applies from today forward, so a past anchor is never a useful default.
  const anchor = hasSessionStart
    ? (sessionStart as Date)
    : hasStart && !isDateBeforeToday(start as Date)
    ? (start as Date)
    : new Date();
  const timeSource = hasSessionStart
    ? (sessionStart as Date)
    : hasStart
    ? (start as Date)
    : null;

  return {
    class_start_date: toDateInputValue(anchor),
    class_start_time: timeSource
      ? `${String(timeSource.getHours()).padStart(2, "0")}:${String(
          timeSource.getMinutes()
        ).padStart(2, "0")}`
      : "",
    class_duration: String(sessionDuration ?? classItem?.duration ?? "60"),
    class_repeat: String(classItem?.recurrence_frequency || "NONE"),
    class_recurrence_interval: String(classItem?.recurrence_interval ?? "1"),
    class_recurrence_week_days: toStringArray(classItem?.recurrence_week_days),
    class_recurrence_month_days: toStringArray(
      classItem?.recurrence_month_days ??
        (classItem?.recurrence_month_day ? [classItem.recurrence_month_day] : [])
    ),
    class_end_mode: endMode,
    class_recurrence_count: String(classItem?.recurrence_count ?? "8"),
    class_recurrence_end: classItem?.recurrence_end
      ? String(classItem.recurrence_end).slice(0, 10)
      : "",
  };
};

const readWeekDays = (values: Record<string, any>): number[] =>
  toStringArray(values.class_recurrence_week_days)
    .map((v) => Number(v))
    .filter((v) => Number.isInteger(v) && v >= 0 && v <= 6);

const readMonthDays = (values: Record<string, any>): number[] =>
  toStringArray(values.class_recurrence_month_days)
    .map((v) => Number(v))
    .filter((v) => Number.isInteger(v) && v >= 1 && v <= 31);

/**
 * On a WEEKLY class the weekday comes from `recurrence_week_days`, not from
 * `start_at` — and nothing projects before `start_at`. So a Wednesday anchor
 * with Tuesday selected quietly makes the *following* Tuesday the first
 * session, and the date the form showed never runs at all. Nudging the anchor
 * forward onto the first selected weekday keeps the two in agreement, which is
 * what the API expects.
 */
export const alignStartToWeekDays = (start: Date, weekDays: number[]): Date => {
  if (weekDays.length === 0 || weekDays.includes(start.getDay())) return start;
  for (let offset = 1; offset <= 7; offset++) {
    const candidate = new Date(start.getTime());
    candidate.setDate(candidate.getDate() + offset);
    if (weekDays.includes(candidate.getDay())) return candidate;
  }
  return start;
};

/**
 * The first session's start and end. Built from a local Date so `toISOString()`
 * carries a real offset — a bare `2026-08-25T18:00:00` is read in the server's
 * timezone, not the organization's, and lands the class on the wrong hour.
 * `end_at - start_at` has to equal `duration` exactly or the API rejects it.
 */
const resolveStartAndEnd = (
  values: Record<string, any>,
  duration: number
): { start: Date; end: Date } | null => {
  const picked = combineDateAndTime(values.class_start_date, values.class_start_time);
  if (!picked) {
    toast.error("Pick a valid first session date and start time.");
    return null;
  }

  let start = picked;
  if (String(values.class_repeat || "NONE") === "WEEKLY") {
    const weekDays = readWeekDays(values);
    if (weekDays.length === 0) {
      toast.error("Select at least one day of the week.");
      return null;
    }
    const aligned = alignStartToWeekDays(start, weekDays);
    if (aligned.getTime() !== start.getTime()) {
      toast.info(
        `First session moved to ${aligned.toLocaleDateString()} — the class only runs on the weekdays you picked.`
      );
      start = aligned;
    }
  }

  // Alignment only ever moves forward, and by less than a week — so it cannot
  // rescue a date picked a month ago. A recurring class anchors projection at
  // start_at and nothing projects before it, which would quietly make the
  // "first session" a date that has already gone.
  if (
    String(values.class_repeat || "NONE") !== "NONE" &&
    isDateBeforeToday(start)
  ) {
    toast.error(
      "A repeating class can't start in the past — pick a date from today onwards."
    );
    return null;
  }

  return { start, end: addMinutes(start, duration) };
};

/**
 * Fills the recurrence half of a create/reschedule payload. Returns false (and
 * has already shown the toast) when the values don't describe a valid rule.
 *
 * The NEVER end mode deliberately sets neither bound — that omission is what
 * the API reads as "open-ended", and it used to be rejected.
 */
const applyRecurrenceFromValues = (
  values: Record<string, any>,
  payload: RecurrencePayload
): boolean => {
  const repeat = String(values.class_repeat || "NONE");
  if (repeat === "NONE") return true;

  const intervalRaw = Number(values.class_recurrence_interval);
  const interval =
    Number.isInteger(intervalRaw) && intervalRaw >= 1 && intervalRaw <= 52
      ? intervalRaw
      : 1;

  payload.recurrence_frequency = repeat as RecurrenceFrequency;
  payload.recurrence_interval = interval;
  // recurrence_start is deliberately not sent: it defaults to start_at's date,
  // and the only value buildable here would be a naive local string.

  if (repeat === "WEEKLY") {
    const weekDays = readWeekDays(values);
    if (weekDays.length === 0) {
      toast.error("Select at least one day of the week.");
      return false;
    }
    payload.recurrence_week_days = weekDays;
  }

  if (repeat === "MONTHLY") {
    const monthDays = readMonthDays(values);
    if (monthDays.length === 0) {
      toast.error("Select at least one day of the month.");
      return false;
    }
    payload.recurrence_month_days = monthDays;
  }

  const endMode = String(values.class_end_mode || "COUNT");
  if (endMode === "DATE") {
    if (!values.class_recurrence_end) {
      toast.error("Pick the date the series ends on.");
      return false;
    }
    payload.recurrence_end = String(values.class_recurrence_end).slice(0, 10);
  } else if (endMode === "COUNT") {
    const count = Number(values.class_recurrence_count);
    if (!Number.isInteger(count) || count < 1 || count > 500) {
      toast.error("Number of sessions must be a whole number between 1 and 500.");
      return false;
    }
    payload.recurrence_count = count;
  }

  return true;
};

/** Validates and builds the create payload; shows a toast and returns null on invalid input. */
export const buildCreateClassPayloadFromValues = (
  values: Record<string, any>
): CreateClassPayload | null => {
  const capacity = Number(values.class_capacity);
  const price = Number(values.class_price);
  const duration = Number(values.class_duration);

  if (!Number.isInteger(capacity) || capacity < 1) {
    toast.error("Capacity must be a whole number of at least 1.");
    return null;
  }
  if (!Number.isFinite(price) || price < 0) {
    toast.error("Price must be a positive number.");
    return null;
  }
  if (!Number.isInteger(duration) || duration < 5) {
    toast.error("Duration must be a whole number of at least 5 minutes.");
    return null;
  }
  if (!values.class_unit_id) {
    toast.error("Pick the unit/court this class runs in.");
    return null;
  }
  if (!values.class_coach_id) {
    toast.error("Pick the coach running this class.");
    return null;
  }

  const firstSession = resolveStartAndEnd(values, duration);
  if (!firstSession) return null;
  const { start, end } = firstSession;

  const payload: CreateClassPayload = {
    unit_id: String(values.class_unit_id),
    coach_id: String(values.class_coach_id),
    category_id: values.class_category_id
      ? String(values.class_category_id)
      : undefined,
    name: String(values.class_name || "").trim(),
    description: values.class_description
      ? String(values.class_description)
      : undefined,
    capacity,
    duration,
    price,
    enrollment_mode: String(
      values.class_enrollment_mode || "BOTH"
    ) as EnrollmentMode,
    start_at: start.toISOString(),
    end_at: end.toISOString(),
  };

  if (!payload.name) {
    toast.error("Class name is required.");
    return null;
  }

  if (!applyRecurrenceFromValues(values, payload)) {
    return null;
  }

  return payload;
};

/**
 * Validates and builds the `PATCH /classes/:id/schedule` payload — when the
 * class runs, from today forward. Shows a toast and returns null on invalid
 * input.
 */
export const buildSchedulePayloadFromValues = (
  values: Record<string, any>
): UpdateSchedulePayload | null => {
  const duration = Number(values.class_duration);
  if (!Number.isInteger(duration) || duration < 5) {
    toast.error("Duration must be a whole number of at least 5 minutes.");
    return null;
  }

  const firstSession = resolveStartAndEnd(values, duration);
  if (!firstSession) return null;

  const payload: UpdateSchedulePayload = {
    start_at: firstSession.start.toISOString(),
    duration,
  };

  if (!applyRecurrenceFromValues(values, payload)) {
    return null;
  }

  return payload;
};

/** Validates and builds the (definition-only) update payload; shows a toast and returns null on invalid input. */
export const buildUpdateClassPayloadFromValues = (
  values: Record<string, any>
): UpdateClassPayload | null => {
  const capacity = Number(values.class_capacity);
  const price = Number(values.class_price);

  if (!Number.isInteger(capacity) || capacity < 1) {
    toast.error("Capacity must be a whole number of at least 1.");
    return null;
  }
  if (!Number.isFinite(price) || price < 0) {
    toast.error("Price must be a positive number.");
    return null;
  }

  return {
    name: String(values.class_name || "").trim(),
    description: values.class_description
      ? String(values.class_description)
      : undefined,
    category_id: values.class_category_id
      ? String(values.class_category_id)
      : undefined,
    coach_id: String(values.class_coach_id),
    capacity,
    price,
    enrollment_mode: String(values.class_enrollment_mode) as EnrollmentMode,
    active: values.class_active === "Active",
  };
};
